import { GraduationCap, BookOpen, Briefcase, Trophy, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import trainingCtaPoster from "@/assets/training-cta-poster.png";
import trainingCtaVideo1 from "@/assets/training-cta-video-1.mp4";
import trainingCtaVideo2 from "@/assets/training-cta-video-2.mp4";

const TrainingSection = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const ctaSlides = [
    { type: "image" as const, src: trainingCtaPoster },
    { type: "video" as const, src: trainingCtaVideo1 },
    { type: "video" as const, src: trainingCtaVideo2 },
  ];
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % ctaSlides.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [ctaSlides.length]);

  const learningPath = [
    {
      levelKey: "training.level.beginner",
      titleKey: "training.course.fundamentals",
      duration: "4",
      skills: ["training.skill.workflow", "training.skill.integrations", "training.skill.nocode"],
    },
    {
      levelKey: "training.level.intermediate",
      titleKey: "training.course.n8n",
      duration: "6",
      skills: ["training.skill.n8n", "training.skill.api", "training.skill.errors"],
    },
    {
      levelKey: "training.level.advanced",
      titleKey: "training.course.agents",
      duration: "8",
      skills: ["training.skill.llm", "training.skill.agents", "training.skill.deployment"],
    },
    {
      levelKey: "training.level.expert",
      titleKey: "training.course.projects",
      duration: "ongoing",
      skills: ["training.skill.client", "training.skill.team", "training.skill.industry"],
    },
  ];

  const benefits = [
    {
      icon: BookOpen,
      titleKey: "training.benefit.curriculum.title",
      descKey: "training.benefit.curriculum.desc",
    },
    {
      icon: Briefcase,
      titleKey: "training.benefit.projects.title",
      descKey: "training.benefit.projects.desc",
    },
    {
      icon: Trophy,
      titleKey: "training.benefit.certification.title",
      descKey: "training.benefit.certification.desc",
    },
  ];

  return (
    <section id="training" className="synra-section relative isolate pb-40 md:pb-56">
      <div className="synra-container">
        {/* Section Header */}
        <div className="synra-section-header">
          <span className="synra-section-label">
            {t("training.label")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t("training.title")}{' '}
            <span className="synra-gradient-text">{t("training.titleHighlight")}</span>
          </h2>
          <p className="mx-auto max-w-2xl text-center text-muted-foreground text-lg">
            {t("training.subtitle")}
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Learning Path */}
          <div className="text-start">
            <div className="flex items-center gap-3 mb-8">
              <GraduationCap className="w-6 h-6 text-primary" />
              <h3 className="font-heading text-xl font-semibold text-foreground">{t("training.path.title")}</h3>
            </div>
            
            <div className="space-y-4">
              {learningPath.map((stage, index) => (
                <div
                  key={stage.titleKey}
                  className="relative ps-8 pb-8 last:pb-0"
                >
                  {/* Connector Line */}
                  {index < learningPath.length - 1 && (
                    <div 
                      className="absolute top-6 w-0.5 h-full bg-border"
                      style={{ insetInlineStart: '0.75rem' }}
                    />
                  )}
                  
                  {/* Step Indicator */}
                  <div 
                    className="absolute top-0 w-6 h-6 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center"
                    style={{ insetInlineStart: 0 }}
                  >
                    <span className="text-xs font-bold text-primary">{index + 1}</span>
                  </div>

                  <div className="synra-card ms-4">
                    <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <span className="text-xs font-medium text-primary uppercase tracking-wider">
                          {t(stage.levelKey)}
                        </span>
                        <h4 className="font-heading font-semibold text-foreground mt-1">
                          {t(stage.titleKey)}
                        </h4>
                      </div>
                      <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                        {stage.duration === "ongoing" ? t("training.duration.ongoing") : `${stage.duration} ${t("training.duration.weeks")}`}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {stage.skills.map((skillKey) => (
                        <span
                          key={skillKey}
                          className="text-xs bg-primary/10 text-primary px-2 py-1 rounded"
                        >
                          {t(skillKey)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits & CTA */}
          <div className="text-start">
            <div className="space-y-6 mb-10">
              {benefits.map((benefit) => (
                <div key={benefit.titleKey} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      {t(benefit.titleKey)}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {t(benefit.descKey)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Card */}
            <div className="synra-card synra-glow border-primary/30 p-5 md:p-6">
              <h4 className="font-heading text-xl font-semibold text-foreground mb-2">
                {t("training.cta.title")}
              </h4>
              <p className="text-muted-foreground text-sm mb-4">
                {t("training.cta.desc")}
              </p>
              <Button variant="synra" size="lg" className="w-full group" onClick={() => navigate("/auth?tab=youth")}>
                <span>{t("training.cta.button")}</span>
                <ArrowRight className={`w-5 h-5 transition-transform duration-200 group-hover:translate-x-1 ${isRTL ? 'rtl-flip' : ''}`} />
              </Button>

              <div className="mt-4 mx-auto w-full max-w-[520px]">
                <div className="relative w-full rounded-xl border border-white/10 bg-background/40 shadow-md p-3 pb-4">
                  <div className="relative w-full">
                  {/* Spacer: matches the active slide's natural ratio */}
                  {ctaSlides.map((slide, idx) =>
                    slide.type === "image" ? (
                      <img
                        key={`spacer-${idx}`}
                        src={slide.src}
                        alt=""
                        aria-hidden
                        className={`block w-full h-auto max-h-[380px] md:max-h-[440px] object-contain ${
                          activeSlide === idx ? "visible" : "invisible"
                        } ${idx === 0 ? "" : "absolute inset-0"}`}
                      />
                    ) : (
                      <video
                        key={`spacer-${idx}`}
                        src={slide.src}
                        muted
                        playsInline
                        preload="metadata"
                        aria-hidden
                        className={`block w-full h-auto max-h-[380px] md:max-h-[440px] object-contain ${
                          activeSlide === idx ? "visible" : "invisible"
                        } ${idx === 0 ? "" : "absolute inset-0"}`}
                      />
                    )
                  )}
                  {/* Cross-fade overlays */}
                  {ctaSlides.map((slide, idx) => (
                    <div
                      key={idx}
                      className={`absolute inset-0 flex items-center justify-center transition-opacity duration-700 ease-in-out ${
                        activeSlide === idx ? "opacity-100" : "opacity-0 pointer-events-none"
                      }`}
                    >
                      {slide.type === "image" ? (
                        <img
                          src={slide.src}
                          alt={t("training.cta.title")}
                          loading="lazy"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <video
                          src={slide.src}
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-contain"
                        />
                      )}
                    </div>
                  ))}
                  </div>

                  <div className="mt-4 flex items-center justify-center gap-2">
                    {ctaSlides.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActiveSlide(idx)}
                        aria-label={`Slide ${idx + 1}`}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          activeSlide === idx
                            ? "w-8 bg-primary"
                            : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/60"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrainingSection;